import { useEffect, useState } from "react";
import { ShipmentError } from "@/components/ShipmentError";
import { useNavigate } from "react-router-dom";
import { api, fmtDateTime } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Truck, Plus, Send, Undo2, Loader2 } from "lucide-react";
import { ShipmentDialog } from "@/components/ShipmentDialog";
import { openAuthedFile, labelFilename } from "@/lib/openFile";
import { ShipmentLabelActions } from "@/components/ShipmentLabelActions";
import { VersandmitteilungButton } from "@/components/VersandmitteilungButton";
import { LabelFormatDialog } from "@/components/LabelFormatDialog";

export default function Versand() {
  const nav = useNavigate();
  const [shipments, setShipments] = useState([]);
  const [orders, setOrders] = useState([]);
  const [dialog, setDialog] = useState(false);
  const [quelle, setQuelle] = useState("");
  const [confirmDialog, setConfirmDialog] = useState(null);
  const [confirmTestMode, setConfirmTestMode] = useState(false);
  const [retourLoading, setRetourLoading] = useState(null);
  const [retourFor, setRetourFor] = useState(null);

  const createRetour = async (s, labelFormat) => {
    setRetourLoading(s.id);
    try {
      const r = await api.post(`/versand/${s.id}/retour`, null, { params: { label_format: labelFormat } });
      toast.success(r.data.tracking_number ? `Retour-Label erstellt · ${r.data.tracking_number}` : "Retour-Label erstellt.");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Retour fehlgeschlagen."); throw e; }
    finally { setRetourLoading(null); }
  };
  const askRetour = (s) => {
    if (s.versandart === "kleinpaket") { toast.error("Retour für DHL Kleinpaket (Warenpost) nicht verfügbar."); return; }
    setRetourFor(s);
  };

  const load = async () => {
    const [s, o] = await Promise.all([api.get("/versand", { params: quelle ? { quelle } : {} }), api.get("/auftraege")]);
    setShipments(s.data);
    setOrders(o.data.filter((x) => ["bezahlt", "abgeschlossen"].includes(x.status) || x.price_locked));
  };
  useEffect(() => { load(); }, [quelle]);

  const sendConfirmation = async () => {
    try {
      await api.post(`/versand/${confirmDialog.id}/versand-bestaetigung`, null, { params: { test_mode: confirmTestMode } });
      toast.success(confirmTestMode ? "Test-Mail eingereiht." : "Versandbestätigung eingereiht.");
      setConfirmDialog(null); setConfirmTestMode(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Fehlgeschlagen."); }
  };

  return (
    <div className="space-y-6" data-testid="versand-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">Versand</h1>
          <p className="text-sm text-slate-500 mt-1">DHL-Labels erzeugen und Versandbestätigungen versenden.</p>
        </div>
        <Button onClick={() => setDialog(true)} data-testid="versand-add-button"><Plus className="w-4 h-4 mr-2" /> Neuer Versand</Button>
      </div>
      <div className="flex gap-1" data-testid="shipments-filter-source">
        {[["", "Alle"], ["auftrag", "Mit Auftrag"], ["manuell", "Manuell"]].map(([v, l]) => (
          <Button key={v || "alle"} size="sm" variant={quelle === v ? "default" : "outline"} onClick={() => setQuelle(v)} data-testid={`shipments-filter-${v || "alle"}`}>{l}</Button>
        ))}
      </div>
      <Card className="border-slate-200">
        <CardContent className="p-0">
          {shipments.length === 0 ? (
            <div className="p-10 text-center text-slate-500"><Truck className="w-10 h-10 mx-auto mb-3 text-slate-400" />Keine Versände.</div>
          ) : (
            <table className="w-full text-sm" data-testid="versand-table">
              <thead>
                <tr className="border-b text-left text-xs uppercase tracking-wider text-slate-500">
                  <th className="px-4 py-3">Auftrag</th><th className="px-4 py-3">Empfänger</th><th className="px-4 py-3">Tracking-Nr.</th><th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Versandart</th><th className="px-4 py-3">Produkt</th><th className="px-4 py-3">Gewicht</th><th className="px-4 py-3">Erstellt</th><th className="px-4 py-3 text-right">Aktionen</th>
                </tr>
              </thead>
              <tbody>
                {shipments.map((s) => (
                  <tr key={s.id} className="border-b border-slate-100 last:border-0" data-testid={`versand-row-${s.id}`}>
                    <td className="px-4 py-3 font-mono text-xs" data-testid={`versand-auftrag-${s.id}`}>{s.order_id ? <button className="underline" onClick={() => nav(`/auftraege/${s.order_id}`)}>{s.auftragsnummer}</button> : <span className="text-slate-500 font-sans">Manuell</span>}</td>
                    <td className="px-4 py-3" data-testid={`versand-empfaenger-${s.id}`}>{s.empfaenger_name || "—"}
                      {s.kunde_id && <button className="block text-xs text-slate-500 underline hover:text-slate-900" onClick={() => nav(`/kunden?kunde=${s.kunde_id}`)} data-testid={`versand-kunde-link-${s.id}`}>Kunde: {s.kunde_name || "öffnen"}</button>}</td>
                    <td className="px-4 py-3 font-mono text-xs">{s.tracking_number || "—"}</td>
                  <td className="px-4 py-3"><Badge variant={s.status === "fehler" ? "destructive" : "outline"}>{s.status}</Badge><ShipmentError error={s.error} details={s.error_details} testid={`versand-error-${s.id}`} /></td>
                    <td className="px-4 py-3"><Badge className="bg-slate-100 text-slate-700 border-slate-300">{s.versandart === "kleinpaket" ? "Kleinpaket (Warenpost)" : "DHL Paket"}</Badge></td>
                    <td className="px-4 py-3 text-slate-700">{s.produkt}</td>
                    <td className="px-4 py-3 text-slate-700">{s.gewicht_kg} kg</td>
                    <td className="px-4 py-3 text-slate-500 text-xs">{fmtDateTime(s.created_at)}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex flex-wrap justify-end gap-1">
                      <ShipmentLabelActions s={s} onChanged={load} />
                      {s.order_id ? <VersandmitteilungButton s={s} onSent={load} /> : (
                        <Button size="sm" variant="outline" onClick={() => setConfirmDialog(s)} data-testid={`versand-confirm-${s.id}`}><Send className="w-4 h-4 mr-1" /> Bestätigung</Button>
                      )}
                      {s.retour_label_url ? (
                        <Button size="sm" variant="outline" onClick={() => openAuthedFile(s.retour_label_url, labelFilename(s, true)).catch((e) => toast.error(`Retour-Label konnte nicht geöffnet werden: ${e.message}`))} data-testid={`versand-retour-label-${s.id}`}><Undo2 className="w-4 h-4 mr-1" /> Retour-Label</Button>
                      ) : s.versandart === "kleinpaket" ? (
                        <span className="inline-flex items-center text-xs text-slate-400 italic" data-testid={`versand-retour-unavailable-${s.id}`} title="Retour für Kleinpaket (Warenpost) nicht verfügbar">Retour n.v.</span>
                      ) : (
                        <Button size="sm" variant="outline" onClick={() => askRetour(s)} disabled={retourLoading === s.id} data-testid={`versand-retour-${s.id}`}>
                          {retourLoading === s.id ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Undo2 className="w-4 h-4 mr-1" />} Retour
                        </Button>
                      )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      <ShipmentDialog open={dialog} onOpenChange={setDialog} orders={orders} onCreated={load} />
      <LabelFormatDialog open={!!retourFor} onOpenChange={(o) => !o && setRetourFor(null)} testId="retour-format-dialog"
        title={`Retour-Label für ${retourFor?.auftragsnummer || retourFor?.empfaenger_name || ""} erzeugen`}
        description="Es wird ein DHL-Retouren-Label (V01PAK mit Retoure-Service) erstellt." confirmLabel="Retour-Label erzeugen"
        defaultFormat={retourFor?.label_format} onConfirm={(f) => createRetour(retourFor, f)} />

      <Dialog open={!!confirmDialog} onOpenChange={(o) => !o && setConfirmDialog(null)}>
        <DialogContent data-testid="versand-bestaetigung-dialog">
          <DialogHeader><DialogTitle>Versandbestätigung senden (manueller Versand)</DialogTitle></DialogHeader>
          <div className="p-3 rounded-md border-2 border-amber-300 bg-amber-50 flex items-start gap-3">
            <div className="flex-1">
              <label className="flex items-center gap-3 cursor-pointer">
                <Switch checked={confirmTestMode} onCheckedChange={setConfirmTestMode} data-testid="versand-test-mode-switch" />
                <span className="text-sm font-medium text-amber-900">🧪 Testmodus — Mail an mich statt an Kunde</span>
              </label>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setConfirmDialog(null)}>Abbrechen</Button><Button onClick={sendConfirmation} data-testid="versand-confirm-send">Senden</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
